import {GraphNode, GraphNodeLink} from '../../api/interfaces/graph-node.interface';
import {set} from 'lodash';
import {EdgeArrowType, MetadataFanOut} from '@maporium-workspace/shared';

export interface MultiElementProperties {
  color?: string;
  // Thickness of node and link
  weight?: number;
  textFontSize?: string;
  name?: string;
  description?: string;
  properties?: {
    textFontSize?: string;
    borderStyle?: string;
    label?: string;
    soundUrl?: string;
  }
  // Exclusion from state
  excludedFromState?: boolean;
  shape?: string;
}

export interface NodeBatchChangeProperties{
  type?: string;
  imageUrl?: string;
  imageOpacity?: string | number;
  imageScale?: string | number;
  scale?: string | number;
  opacity?: string | number;
  colorOpacity?: string | number;
  soundUrl?: string;
  label?: string;
  textFontSize?: string | number;
}

export interface LinkBatchChangeProperties {
  // links shape
  type?: string;
  // links Stroke
  shape?: string;
  direction?: string;
  // links arrow head style
  edgeArrowType?: EdgeArrowType;
  // link label slope
  labelSlope?: 'straight' | 'sloped' | 'curved';
  // link label position
  linkLabelPosition?: number;
  // link label offset
  linkLabelOffset?: number;
  // link label size
  textFontSize?: string | number;
  // link label source
  label?: string;
  // link sound
  soundUrl?: string | null;
}

export interface MultiUpdateEvent {
  node?: Partial<GraphNode>,
  general?: Partial<MultiElementProperties>
  link?: Partial<GraphNodeLink>
  metadata?: MetadataFanOut
}

export function getMultiElementPropertiesFromDiff(diff: Partial<MultiElementProperties>, intentionallyChanged?: Set<string>) {
  const multiElementProperties: MultiElementProperties = {};

  // If intentionallyChanged is provided, only include properties that were actually changed by the user
  const isAllowed = (key: string) => !intentionallyChanged || intentionallyChanged.has(key);

  if (diff.color !== undefined && isAllowed('color')) {
    multiElementProperties.color = diff.color;
  }

  if (diff.name !== undefined && isAllowed('name')) {
    multiElementProperties.name = diff.name;
  }

  if (diff.description !== undefined && isAllowed('description')) {
    multiElementProperties.description = diff.description;
  }

  if (diff.weight !== undefined && isAllowed('weight')) {
    multiElementProperties.weight = diff.weight;
  }

  if (diff.textFontSize !== undefined && isAllowed('properties.textFontSize')) {
    set(multiElementProperties, 'textFontSize', diff.textFontSize);
    set(multiElementProperties, 'properties.textFontSize', diff.textFontSize)
  }

  if (diff.properties !== undefined) {
    if (diff.properties.textFontSize !== undefined && isAllowed('properties.textFontSize')) {
      set(multiElementProperties, 'properties.textFontSize', diff.properties.textFontSize);
      set(multiElementProperties, 'textFontSize', diff.properties.textFontSize);
    }
  }
  if (diff.shape !== undefined && isAllowed('properties.borderStyle')) {
    set(multiElementProperties, 'shape', diff.shape);
    set(multiElementProperties, 'properties.borderStyle', diff.shape);
  }

  if (diff.properties?.borderStyle !== undefined && isAllowed('properties.borderStyle')) {
    set(multiElementProperties, 'properties.borderStyle', diff.properties.borderStyle);
    set(multiElementProperties, 'shape', diff.properties.borderStyle);
  }

  if (diff.properties?.label !== undefined && isAllowed('properties.label')) {
    set(multiElementProperties, 'properties.label', diff.properties.label);
  }

  if (diff.properties?.soundUrl !== undefined && isAllowed('properties.soundUrl')) {
    set(multiElementProperties, 'properties.soundUrl', diff.properties.soundUrl);
  }

  if (diff.excludedFromState !== undefined && isAllowed('excludedFromState')) {
    multiElementProperties.excludedFromState = diff.excludedFromState;
  }

  return multiElementProperties;
}

export function getNodeBatchChangePropertiesFromDiff(diff: Partial<GraphNode>, intentionallyChangedProperties?: Set<string>) {
  const nodeBatchChangeProperties: NodeBatchChangeProperties = {};

  if (diff.type !== undefined && (!intentionallyChangedProperties || intentionallyChangedProperties.has('type'))) {
    nodeBatchChangeProperties.type = diff.type;
  }

  if (diff.imageUrl !== undefined && (!intentionallyChangedProperties || intentionallyChangedProperties.has('imageUrl'))) {
    nodeBatchChangeProperties.imageUrl = diff.imageUrl;
  }

  if (diff.properties !== undefined) {
    if (diff.properties.scale !== undefined && (!intentionallyChangedProperties || intentionallyChangedProperties.has('properties.scale'))) {
      set(nodeBatchChangeProperties, 'properties.scale', diff.properties.scale);
    }

    if (diff.properties.opacity !== undefined && (!intentionallyChangedProperties || intentionallyChangedProperties.has('properties.opacity'))) {
      set(nodeBatchChangeProperties, 'properties.opacity', diff.properties.opacity);
    }

    if (diff.properties.colorOpacity !== undefined && (!intentionallyChangedProperties || intentionallyChangedProperties.has('properties.colorOpacity'))) {
      set(nodeBatchChangeProperties, 'properties.colorOpacity', diff.properties.colorOpacity);
    }

    if (diff.properties.imageOpacity !== undefined && (!intentionallyChangedProperties || intentionallyChangedProperties.has('properties.imageOpacity'))) {
      set(nodeBatchChangeProperties, 'properties.imageOpacity', diff.properties.imageOpacity);
    }

    if (diff.properties.imageScale !== undefined && (!intentionallyChangedProperties || intentionallyChangedProperties.has('properties.imageScale'))) {
      set(nodeBatchChangeProperties, 'properties.imageScale', diff.properties.imageScale);
    }

    if (diff.properties.textMarginX !== undefined && (!intentionallyChangedProperties || intentionallyChangedProperties.has('properties.textMarginX'))) {
      set(nodeBatchChangeProperties, 'properties.textMarginX', diff.properties.textMarginX);
    }

    if (diff.properties.textMarginY !== undefined && (!intentionallyChangedProperties || intentionallyChangedProperties.has('properties.textMarginY'))) {
      set(nodeBatchChangeProperties, 'properties.textMarginY', diff.properties.textMarginY);
    }

    if (diff.properties.textValign !== undefined && (!intentionallyChangedProperties || intentionallyChangedProperties.has('properties.textValign'))) {
      set(nodeBatchChangeProperties, 'properties.textValign', diff.properties.textValign);
    }

    if (diff.properties.textHalign !== undefined && (!intentionallyChangedProperties || intentionallyChangedProperties.has('properties.textHalign'))) {
      set(nodeBatchChangeProperties, 'properties.textHalign', diff.properties.textHalign);
    }

    if (diff.properties.borderStyle !== undefined && (!intentionallyChangedProperties || intentionallyChangedProperties.has('properties.borderStyle'))) {
      set(nodeBatchChangeProperties, 'properties.borderStyle', diff.properties.borderStyle);
    }

    if (diff.properties.soundUrl !== undefined && (!intentionallyChangedProperties || intentionallyChangedProperties.has('properties.soundUrl'))) {
      set(nodeBatchChangeProperties, 'properties.soundUrl', diff.properties.soundUrl);
    }

    if (diff.properties.label !== undefined && (!intentionallyChangedProperties || intentionallyChangedProperties.has('properties.label'))) {
      set(nodeBatchChangeProperties, 'properties.label', diff.properties.label);
    }

    // Always include textFontSize if it's in the diff - it's a user-visible style change
    if (diff.properties.textFontSize !== undefined) {
      set(nodeBatchChangeProperties, 'properties.textFontSize', diff.properties.textFontSize);
    }
  }

  return nodeBatchChangeProperties;
}

export function getLinkBatchChangePropertiesFromDiff(diff: Partial<GraphNodeLink>) {
  const linkBatchChangeProperties: LinkBatchChangeProperties = {};

  if (diff.type !== undefined) {
    linkBatchChangeProperties.type = diff.type;
  }

  if (diff.shape !== undefined) {
    linkBatchChangeProperties.shape = diff.shape;
  }

  if (diff.direction !== undefined) {
    linkBatchChangeProperties.direction = diff.direction;
  }

  if (diff.edgeArrowType !== undefined) {
    linkBatchChangeProperties.edgeArrowType = diff.edgeArrowType;
  }

  if (diff.labelSlope !== undefined) {
    linkBatchChangeProperties.labelSlope = diff.labelSlope;
  }

  if (diff.linkLabelPosition !== undefined) {
    linkBatchChangeProperties.linkLabelPosition = diff.linkLabelPosition;
  }

  if (diff.linkLabelOffset !== undefined) {
    linkBatchChangeProperties.linkLabelOffset = diff.linkLabelOffset;
  }

  if (diff.textFontSize !== undefined) {
    linkBatchChangeProperties.textFontSize = diff.textFontSize;
  }

  if (diff.label !== undefined) {
    linkBatchChangeProperties.label = diff.label;
  }

  if (diff.soundUrl !== undefined) {
    linkBatchChangeProperties.soundUrl = diff.soundUrl;
  }

  return linkBatchChangeProperties;
}
